import { cva, type VariantProps } from "class-variance-authority";
import { AnimatePresence, LayoutGroup } from "motion/react";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import type { To } from "react-router-dom";

import { NewNotificationsPill } from "./NewNotificationsPill";
import { NotificationRow, type NotificationRowProps, type NotificationRowSelection } from "./NotificationRow";
import { NotificationRowSkeleton } from "./NotificationRowSkeleton";
import { formatDateLabel, groupNotificationsByDate } from "./utils";
import type { useNotificationsPage } from "../api/useNotificationsPage";
import { useNewNotificationTracker } from "../hooks/useNewNotificationTracker";
import type { NotificationData } from "../types";

import { Badge } from "@/components/ui/v2/badge";
import { Separator } from "@/components/ui/v2/separator";
import { cn } from "@/lib/utils/cn";

const feedScrollVariants = cva("overflow-y-auto", {
  variants: {
    surface: {
      popover: "max-h-96",
      page: "h-full",
    },
  },
});

const feedDateHeaderVariants = cva("sticky top-0 z-10 flex items-center gap-3 px-4 py-2", {
  variants: {
    surface: {
      popover: "bg-card",
      page: "bg-background",
    },
  },
});

type NotificationFeedProps = Pick<ReturnType<typeof useNotificationsPage>, "fetchNextPage" | "hasNextPage" | "isFetchingNextPage"> &
  Required<VariantProps<typeof feedScrollVariants>> & {
    notifications: NotificationData[];
    scrollTargetId: string;
    className?: string;
    onOpen?: NotificationRowProps["onOpen"];
    onAction: NotificationRowProps["onAction"];
    selection?: Pick<NotificationRowSelection, "onToggle"> & {
      isChecked: (notification: NotificationData) => boolean;
    };
    selectedId?: string;
    getRowHref: (notification: NotificationData) => To | undefined;
  };

export function NotificationFeed({
  notifications,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  surface,
  scrollTargetId,
  className,
  onOpen,
  onAction,
  selection,
  selectedId,
  getRowHref,
}: NotificationFeedProps) {
  const { t, i18n } = useTranslation();
  const grouped = useMemo(() => groupNotificationsByDate(notifications), [notifications]);
  const { scrollRef, handleScroll, newIds, pendingCount, scrollToTop } =
    useNewNotificationTracker(notifications);

  return (
    <div className={cn("relative", className)}>
      <div
        ref={scrollRef}
        id={scrollTargetId}
        tabIndex={-1}
        className={cn(feedScrollVariants({ surface }), "relative outline-none")}
        onScroll={handleScroll}
      >
        <InfiniteScroll
          dataLength={notifications.length}
          next={fetchNextPage}
          hasMore={hasNextPage}
          scrollThreshold={0.9}
          scrollableTarget={scrollTargetId}
          style={{ overflow: "visible" }}
          loader={
            isFetchingNextPage ? (
              <div className="divide-y border-t" aria-busy="true">
                <NotificationRowSkeleton />
              </div>
            ) : null
          }
        >
          <LayoutGroup>
            {grouped.map((group) => (
              <Fragment key={group.dateKey}>
                <div data-slot="notification-date-header" className={feedDateHeaderVariants({ surface })}>
                  <Separator className="flex-1" />
                  <Badge variant="outline">{formatDateLabel(group.date, i18n.language, t)}</Badge>
                  <Separator className="flex-1" />
                </div>
                <AnimatePresence initial={false} mode="popLayout">
                  {group.notifications.map((notification) => (
                    <NotificationRow
                      key={notification.id}
                      notification={notification}
                      onOpen={onOpen}
                      onAction={onAction}
                      selection={
                        selection && {
                          isChecked: selection.isChecked(notification),
                          onToggle: selection.onToggle,
                        }
                      }
                      isNew={newIds.has(notification.id)}
                      isSelected={notification.id === selectedId}
                      href={getRowHref(notification)}
                    />
                  ))}
                </AnimatePresence>
              </Fragment>
            ))}
          </LayoutGroup>
        </InfiniteScroll>
      </div>
      <NewNotificationsPill count={pendingCount} onClick={scrollToTop} />
    </div>
  );
}
